import { Injectable } from '@angular/core';
import * as jsPDF from 'jspdf';
import * as html2canvas from 'html2canvas';
import { Observable, Subject } from 'rxjs';

@Injectable()
export class Dom2PdfService {

    private defaultModifiers = [
        { selector: '.show-on-print', action: 'updateStyle', style: 'display', value: 'block' }
    ]

    pdf: Subject<any> = new Subject();

    /**
     * @description Prints an HTML element to PDF. Add "data-html2canvas-ignore" attribute
     * to elements that you don't want to display on PDF.
     * @param elementsToPrint
     * @param modifiers Modifiers of elements to be modified before print
     * @param config Configuration object
     * @returns {void}
     */
    download(elementsToPrint, modifiers = [], config: any = {}, shouldSavePDF = true) {
        elementsToPrint = elementsToPrint && elementsToPrint.filter(e => !!e);
        if (!config.pdfName) {
            config.pdfName = 'San Cristobal Seguros - Impresión';
        }
        if (!config.orientation) {
            config.orientation = 'portrait';
        }

        // Generate options to be used by html2Canvas library
        const html2CanvasOptions: any = {
            // onClone is a library built-in method executed before generate a canvas
            onclone: elementToPrint => this.modifyElementBeforePrint(elementToPrint, modifiers),
            logging: false
        }

        // If canvas must be rendered on a specific width or height
        // then add it to html2Canvas options
        if (config.windowWidth) {
            html2CanvasOptions.windowWidth = config.windowWidth;
        }
        if (config.windowHeight) {
            html2CanvasOptions.windowHeight = config.windowHeight;
        }

        const pdf: Subject <any> = new Subject();

        // Create and download pdf
        this.generateCanvas(elementsToPrint, html2CanvasOptions)
            .subscribe(arrayCanvas => {
                pdf.next(this.downloadPdf(arrayCanvas, config, shouldSavePDF));
            })
            return pdf;
    }

    generateCanvas(elementsToPrint, html2CanvasOptions) {
        const arrayCanvas = [];
        const elementsAmount = elementsToPrint.length - 1;

        return new Observable(observer => {
            elementsToPrint.forEach((element, index) => {
                html2canvas(element, html2CanvasOptions)
                    .then(canvas => {
                        arrayCanvas.push(canvas);
                        if (index === elementsAmount) {
                            observer.next(arrayCanvas);
                            observer.complete();
                        }
                    });
            });
        });
    }

    modifyElementBeforePrint(elementToPrint, modifiers) {

        // Concat default modifiers with current modifiers
        modifiers = modifiers.concat(this.defaultModifiers);

        // Check if there are elements to modify before print
        if (modifiers.length) {
            modifiers.forEach(modifier => {
                // Find elements by selector
                const elements = elementToPrint && elementToPrint.querySelectorAll(modifier.selector) || [];
                // For each found element modify its style
                elements.forEach((e) => {
                    switch (modifier.action) {
                        case 'updateStyle':
                            e.style[modifier.style] = modifier.value;
                            break;
                        case 'addClass':
                            e.classList.add(modifier.value);
                            break;
                        case 'removeClass':
                            e.classList.remove(modifier.value);
                            break;
                    }
                })
            })
        }
    }

    downloadPdf(arrayCanvas, config, shouldSavePDF) {

        const pdfOptions = {
            height: config.orientation === 'portrait' ? 297 : 210,
            width: config.orientation === 'portrait' ? 210 : 297,
            size: 'a4',
            unit: 'mm',
            minimumMargin: 5
        };
        const pdf = new jsPDF(config.orientation, pdfOptions.unit, pdfOptions.size);
        const elementsAmount = arrayCanvas.length - 1;

        arrayCanvas.forEach((canvas, index) => {
            const img = canvas.toDataURL('image/jpeg');
            const imgResized = this.calculateAspectRatioFit(canvas.width, canvas.height, pdfOptions);
            const sidesMargin = this.calculateMargin(imgResized.width, 'width', pdfOptions);
            pdf.addImage(img, 'JPEG', sidesMargin, pdfOptions.minimumMargin, imgResized.width, imgResized.height);
            if (index < elementsAmount) {
                pdf.addPage();
            }
        })
        if (shouldSavePDF) {
            pdf.save(config.pdfName);
        }
        return pdf;
    }

    calculateAspectRatioFit(srcWidth, srcHeight, options) {
        const maxWidth = options.width - (options.minimumMargin * 4);
        const maxHeight = options.height - (options.minimumMargin * 4);
        const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
        return { width: srcWidth * ratio, height: srcHeight * ratio };
    }

    calculateMargin(toPrintSize: number, measurement: string, options) {
        return (options[measurement] - toPrintSize) / 2;
    }
}
